import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys, type OttoClient } from "@otto/plattform";

export function useSchool(client: OttoClient, schoolId: string) {
  return useQuery({
    queryKey: queryKeys.schule(schoolId),
    enabled: schoolId !== "",
    queryFn: async () => {
      const { data, error } = await client
        .from("schools")
        .select("id, name, license_until")
        .eq("id", schoolId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useTeachers(client: OttoClient, schoolId: string) {
  return useQuery({
    queryKey: queryKeys.teachers(schoolId),
    enabled: schoolId !== "",
    queryFn: async () => {
      const { data, error } = await client
        .from("profiles")
        .select("id, display_name, role")
        .order("display_name");
      if (error) throw error;
      return data ?? [];
    },
  });
}

export type InviteError =
  | "not_signed_in"
  | "school_admin_only"
  | "invalid_email"
  | "name_missing"
  | "already_invited"
  | "invite_failed"
  | "email_rate_limit"
  | "unbekannt";

/**
 * Der Fehler einer Einladung: Code als `message`, HTTP-Status daneben.
 *
 * Der Status reist mit, weil ein unbekannter Code sonst als „fehlgeschlagen“
 * beim Schuladmin landet und niemand mehr sagen kann, woran es lag.
 */
export type InviteFailure = Error & { readonly status?: number };

/**
 * Einladen läuft über eine Edge Function, nicht über den Client.
 *
 * Ein Konto anzulegen geht nur mit dem Service-Key, und der darf nie in einen
 * Browser – mit ihm umgeht man jede Policy. Die Funktion prüft selbst, dass der
 * Aufrufer Schuladmin ist, und hängt das neue Profil an die Schule des
 * Aufrufers.
 */
export function useInviteTeacher(client: OttoClient, schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      email: string;
      name: string;
      role: "teacher" | "school_admin";
    }) => {
      const { data, error } = await client.functions.invoke<{
        ok?: boolean;
        error?: string;
      }>("invite-teacher", { body: input });
      if (error) {
        // Die Funktion antwortet mit einem Fehlercode im Körper; der ist
        // nützlicher als "FunctionsHttpError".
        let code: InviteError = "unbekannt";
        const response = (error as { context?: Response }).context;
        if (response) {
          const parsed = (await response.json().catch(() => ({}))) as { error?: string };
          code = (parsed.error as InviteError) ?? "unbekannt";
        }
        // Ohne `response` gar keine Eigenschaft statt einer mit `undefined`:
        // `exactOptionalPropertyTypes` unterscheidet das, und zu Recht – „nicht
        // bekannt“ und „bekannt als undefiniert“ sind zwei Aussagen.
        const failure: InviteFailure = Object.assign(
          new Error(code),
          response ? { status: response.status } : {},
        );
        throw failure;
      }
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.teachers(schoolId) }),
  });
}
